import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trash2, AlertTriangle, CheckCircle2, Loader2, Shield } from 'lucide-react';
import SEOHead from './SEOHead';

const API_BASE_URL = (() => {
  const hostname = window.location.hostname;
  if (hostname === 'digihr.in' || hostname === 'www.digihr.in') {
    return 'https://digihr.in/api';
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:5015/api';
  }
  return '/api';
})();

const DeleteAccount = () => {
  const [step, setStep] = useState(1); // 1=form, 2=confirm, 3=success
  const [formData, setFormData] = useState({
    organizationCode: '',
    email: '',
    password: '',
    reason: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.organizationCode.trim() || !formData.email.trim() || !formData.password.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setStep(2);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/auth/delete-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationCode: formData.organizationCode.trim(),
          email: formData.email.trim(),
          password: formData.password,
          reason: formData.reason.trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete account');
      }
      setStep(3);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead title="Delete Account" description="Request deletion of your DigiHR account and associated personal data." canonical="/delete-account" noindex />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Trash2 className="w-8 h-8 text-red-600" />
            <h1 className="text-3xl font-bold text-gray-900">Delete Account</h1>
          </div>
          <p className="text-gray-500">Permanently delete your DIGIHR account and all associated data.</p>
        </div>

        {/* Step 1: Form */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            {/* Warning */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800 mb-1">Warning: This action is permanent</h3>
                <p className="text-red-700 text-sm">
                  Deleting your account will permanently remove all your data including:
                </p>
                <ul className="text-red-700 text-sm mt-2 space-y-1 list-disc pl-5">
                  <li>Personal profile and employment information</li>
                  <li>Attendance records, location logs, and selfie photos</li>
                  <li>Leave history and balances</li>
                  <li>Payroll records and salary information</li>
                  <li>All other data associated with your account</li>
                </ul>
                <p className="text-red-700 text-sm mt-2 font-medium">
                  This action cannot be undone. Please make sure you want to proceed.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="organizationCode"
                  value={formData.organizationCode}
                  onChange={handleChange}
                  placeholder="e.g., TECH01"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@company.com"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your account password"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Deletion <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Please tell us why you want to delete your account..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none resize-none"
                />
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition-colors"
              >
                Continue to Delete Account
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-500">
                  Your credentials are used solely to verify your identity for this deletion request. 
                  Read our <Link to="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</Link> for more details.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Confirmation */}
        {step === 2 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Are you absolutely sure?</h2>
            <p className="text-gray-600 mb-6">
              You are about to permanently delete the account associated with <strong>{formData.email}</strong>. 
              All data will be erased and cannot be recovered.
            </p>

            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => { setStep(1); setError(''); }}
                disabled={loading}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-lg transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={loading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    Yes, Delete My Account
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Deletion Initiated</h2>
            <p className="text-gray-600 mb-4">
              Your account deletion request has been submitted successfully. Your account and all associated data will be 
              permanently deleted within 30 days.
            </p>
            <p className="text-gray-500 text-sm mb-6">
              If you did not request this or wish to cancel, please contact your organization administrator or 
              email us at <a href="mailto:support@digihr.in" className="text-blue-600 hover:underline">support@digihr.in</a> within 
              30 days.
            </p>
            <Link
              to="/"
              className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
            >
              Return to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeleteAccount;
